import {
    getMembershipDiscountLineItem,
    getPaymentLineItems,
    getPurchaseItemsHavingMembershipCodeDiscount,
    isExperiencePurchaseItem,
    isMerchandiseItem,
    PurchaseActionOption,
} from "common";
import { sumBy } from "lodash";
import React, { useState } from "react";
import { Event, ExperiencePurchaseItem, Guest, MerchandisePurchaseItem, Purchase, PurchaseItem } from "types";
import { CancelPurchaseItemModal } from "@/components/Dashboard/CancelPurchaseItemModal";
import { shouldReturnExcessAtPurchaseLevel } from "@/components/Dashboard/getRefundOptionAfterCancel";
import { RefundOption } from "@/components/Dashboard/RefundOption";
import { RefundOptionsModal } from "@/components/Dashboard/RefundOptionsModal";
import { RemoveMembershipCodeModal } from "@/components/Dashboard/RemoveMembershipCodeModal";
import { UnassignEventGuidePrompt } from "@/components/Dashboard/Resources/Guides/UnassignEventGuides";
import { SwitchExperienceModal } from "@/components/Dashboard/SwitchExperienceModal";
import { PurchaseChangeQuantityModal } from "@/components/PurchaseModification/ChangeQuantity/PurchaseChangeQuantityModal";
import {
    CollectBalanceOption,
    CollectBalanceOptionsModal,
} from "@/components/PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "@/components/PurchaseModification/CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "@/components/PurchaseModification/CollectPurchaseBalanceModal";
import { EditCustomLineItemListModal } from "@/components/PurchaseModification/EditCustomLineItemsModal";
import { EditPurchaseAddOnsModal } from "@/components/PurchaseModification/EditPurchaseAddOnsModal";
import { MessagePurchaseModal } from "@/components/PurchaseModification/MessagePurchaseModal";
import { PurchaseChangeArrivalModal } from "@/components/PurchaseModification/PurchaseChangeArrivalModal";
import { PurchaseReduceBookingValueModal } from "@/components/PurchaseModification/Refund/PurchaseReduceBookingValueModal";
import {
    PurchaseReturnPaymentModal,
    ReturnPaymentSource,
} from "@/components/PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { SplitPayRefundModal } from "@/components/PurchaseModification/SplitPayRefundModal";
import { UpdateMerchandiseQuantityModal } from "@/components/PurchaseModification/UpdateQuantity/UpdateMerchandiseQuantityModal";
import { LinkWaiverModal } from "@/components/PurchaseModification/Waiver/LinkWaiverModal";
import { LinkWaiverParticipantModal } from "@/components/PurchaseModification/Waiver/LinkWaiverParticipantModal";
import { isOneBuyerWithSamePaymentMethod } from "@/models/Purchase";

interface CancelReservationModalData {
    showRefundModal: boolean;
    newPurchase?: Purchase;
    events?: Event[];
}

interface ItemActionModalsState {
    purchase: Purchase;
    event?: Event;
    selectedOption: PurchaseActionOption | null;
    selectedRefundOption: RefundOption | null;
    refundAmount: number;
    returnPaymentSource: ReturnPaymentSource | null;
    modifiedPurchase: Purchase | null;
    shouldShowUnassignGuide: boolean;
    cancelReservationModalData: CancelReservationModalData | null;
}

interface ItemActionModalsActions {
    onModalClose: () => void;
    onCloseAfterPurchaseChange: (newPurchase: Purchase | undefined) => void;
    onChangeArrivalContinue: (previousEvent: Event, updatedPurchase?: Purchase) => void;
    onPurchaseInvalidate: () => void;
    onFlushPurchaseCache: () => void;
    onResetRefundOption: () => void;
    onCancelReservationClose: (showRefundModal: boolean, newPurchase?: Purchase, events?: Event[]) => void;
    setSelectedOption: (option: PurchaseActionOption | null) => void;
    setSelectedRefundOption: (option: RefundOption | null) => void;
    setRefundAmount: (amount: number) => void;
    setReturnPaymentSource: (source: ReturnPaymentSource | null) => void;
    setModifiedPurchase: (purchase: Purchase | null) => void;
    setCancelReservationModalData: (data: CancelReservationModalData | null) => void;
}

interface ItemActionModalsProps {
    menuState: ItemActionModalsState;
    menuActions: ItemActionModalsActions;
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
    guest?: Guest;
    onClose?: () => void;
}

const shouldRenderMerchandiseQuantityModal = (
    item: ExperiencePurchaseItem | MerchandisePurchaseItem,
    selectedRefundOption: RefundOption | null,
    merchandiseItem?: MerchandisePurchaseItem,
) => {
    return (
        !!merchandiseItem &&
        (isMerchandiseItem(item) || selectedRefundOption === RefundOption.CHANGE_MERCHANDISE_QUANTITY)
    );
};

export const ItemActionModals = ({ menuState, menuActions, item, guest, onClose }: ItemActionModalsProps) => {
    const [refundItem, setRefundItem] = useState<PurchaseItem | null>(null);
    const {
        purchase,
        event,
        selectedOption,
        selectedRefundOption,
        refundAmount,
        returnPaymentSource,
        modifiedPurchase,
        shouldShowUnassignGuide,
        cancelReservationModalData,
    } = menuState;
    const {
        onModalClose,
        onCloseAfterPurchaseChange,
        onChangeArrivalContinue,
        onPurchaseInvalidate,
        onFlushPurchaseCache,
        onResetRefundOption,
        onCancelReservationClose,
        setSelectedOption,
        setSelectedRefundOption,
        setRefundAmount,
        setReturnPaymentSource,
        setModifiedPurchase,
        setCancelReservationModalData,
    } = menuActions;

    const experienceItemForGuestQuantity = isExperiencePurchaseItem(item)
        ? item
        : purchase.items.find((purchaseItem): purchaseItem is ExperiencePurchaseItem =>
              isExperiencePurchaseItem(purchaseItem),
          );

    const merchandiseItem = isMerchandiseItem(item)
        ? item
        : purchase.items.find((purchaseItem): purchaseItem is MerchandisePurchaseItem =>
              isMerchandiseItem(purchaseItem),
          );

    const handleMoveToUnassignGuideOrInvalidate = () => {
        if (shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        onPurchaseInvalidate();
        setSelectedOption(null);
    };

    const handleMoveToUnassignGuideOrInvalidateAndFlush = () => {
        if (shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        onFlushPurchaseCache();
        setSelectedOption(null);
        onPurchaseInvalidate();
    };

    const handleRefundOptionsClose = () => {
        onFlushPurchaseCache();
        setSelectedOption(null);
        onPurchaseInvalidate();
    };

    const handleRefundOptionsConfirm = (refundOption: RefundOption, selectedPurchase: Purchase) => {
        const amount =
            selectedPurchase.balance < 0
                ? selectedPurchase.balance
                : sumBy(getPaymentLineItems(selectedPurchase), "amount");
        setRefundAmount(Math.abs(amount));
        setSelectedRefundOption(refundOption);
        setSelectedOption(null);
    };

    const handleCollectBalanceOptionsConfirm = (action: CollectBalanceOption) => {
        if (action === CollectBalanceOption.COLLECT_NOW) {
            setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
            return;
        }

        if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
            setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
            return;
        }

        if (shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        setSelectedOption(null);
        onPurchaseInvalidate();
    };

    const handleChangeQuantityModalClose = (
        nextModalOption: PurchaseActionOption | null,
        nextRefundOption: RefundOption | null,
        nextRefundAmount: number,
        newPurchase?: Purchase,
        nextRefundItem?: PurchaseItem,
    ) => {
        if (newPurchase) {
            setModifiedPurchase(newPurchase);
        }

        setSelectedOption(nextModalOption);
        setSelectedRefundOption(nextRefundOption);
        setReturnPaymentSource(
            nextRefundOption === RefundOption.RETURN_PAYMENT ? ReturnPaymentSource.CHANGE_GUEST_QUANTITY : null,
        );
        setRefundItem(nextRefundItem ?? null);

        if (!nextModalOption && !nextRefundOption) {
            onPurchaseInvalidate();
        }

        setRefundAmount(nextRefundAmount);
    };

    const handleCancelPurchaseItemModalClose = (
        type: "skip" | "confirm",
        showRefundModal: boolean,
        newPurchase?: Purchase,
        events?: Event[],
    ) => {
        if (type === "confirm") {
            const showRemoveMembershipCodeModal = item
                ? !!getMembershipDiscountLineItem(item)
                : getPurchaseItemsHavingMembershipCodeDiscount(purchase).length > 0;

            if (showRemoveMembershipCodeModal) {
                setCancelReservationModalData({ showRefundModal, newPurchase, events });
                setSelectedOption(PurchaseActionOption.REMOVE_MEMBERSHIP_CODE);
                return;
            }
        }

        onCancelReservationClose(showRefundModal, newPurchase, events);
    };

    const handleRemoveMembershipCodeModalClose = (updatedPurchase?: Purchase) => {
        if (cancelReservationModalData) {
            const { showRefundModal, newPurchase, events } = cancelReservationModalData;
            onCancelReservationClose(showRefundModal, updatedPurchase ?? newPurchase, events);
        }
    };

    const handleSplitPayRefundClose = () => {
        setSelectedRefundOption(null);
        if (shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        onClose?.();
    };

    const handlePurchaseReturnPaymentClose = () => {
        setRefundItem(null);
        setSelectedRefundOption(null);
        if (shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
        } else {
            onResetRefundOption();
        }
        onFlushPurchaseCache();
    };

    const handleEditAddOnsModalClose = (
        nextModalOption: PurchaseActionOption | null,
        nextRefundOption: RefundOption | null,
        newPurchase?: Purchase,
        nextRefundItem?: PurchaseItem,
    ) => {
        if (newPurchase) {
            setModifiedPurchase(newPurchase);
        }

        setSelectedOption(nextModalOption);
        setSelectedRefundOption(nextRefundOption);
        setReturnPaymentSource(
            nextRefundOption === RefundOption.RETURN_PAYMENT ? ReturnPaymentSource.CHANGE_ADD_ONS : null,
        );
        setRefundItem(nextRefundItem ?? null);
        setRefundAmount(Math.abs(newPurchase?.balance ?? 0));

        if (!nextModalOption && !nextRefundOption) {
            onPurchaseInvalidate();
        }
    };

    const handleUnassignGuidePromptClose = () => {
        onFlushPurchaseCache();
        setSelectedOption(null);
        onPurchaseInvalidate();
    };

    // When cancelling a MIST item that has no value left, drop the item so the modal returns the
    // purchase-level overpayment instead of the item-scoped modal, which would cap the refund at $0.
    const returnExcessAtPurchaseLevel = shouldReturnExcessAtPurchaseLevel(
        modifiedPurchase ?? purchase,
        item ?? refundItem,
        selectedRefundOption,
    );
    const refundModalItem = returnExcessAtPurchaseLevel ? undefined : item ?? refundItem;
    const refundModalAmount = returnExcessAtPurchaseLevel
        ? Math.abs((modifiedPurchase ?? purchase).balance)
        : Math.abs(refundAmount);

    return (
        <>
            <RefundOptionsModal
                purchase={modifiedPurchase ?? purchase}
                isOpen={selectedOption === PurchaseActionOption.REFUND}
                onClose={handleRefundOptionsClose}
                onConfirm={handleRefundOptionsConfirm}
            />

            {/* This is the modal that allows you to choose the correct way to collect balance */}
            <CollectBalanceOptionsModal
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_OPTIONS}
                purchase={modifiedPurchase ?? purchase}
                onClose={handleMoveToUnassignGuideOrInvalidate}
                onConfirm={handleCollectBalanceOptionsConfirm}
            />

            <MessagePurchaseModal
                isTravelersSelectable
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.MESSAGE}
                onClose={onModalClose}
            />

            {isExperiencePurchaseItem(item) && (
                <PurchaseChangeArrivalModal
                    purchase={purchase}
                    item={item}
                    isOpen={selectedOption === PurchaseActionOption.CHANGE_ARRIVAL}
                    onClose={(newPurchase, _redirectUrl, nextRefundItem) => {
                        if (newPurchase?.balance && newPurchase.balance < 0) {
                            setReturnPaymentSource(ReturnPaymentSource.CHANGE_ARRIVAL);
                            setRefundItem(nextRefundItem ?? null);
                        } else {
                            setReturnPaymentSource(null);
                            setRefundItem(null);
                        }

                        onCloseAfterPurchaseChange(newPurchase);
                    }}
                    onContinue={onChangeArrivalContinue}
                />
            )}

            {experienceItemForGuestQuantity && (
                <PurchaseChangeQuantityModal
                    purchase={purchase}
                    item={experienceItemForGuestQuantity}
                    isOpen={
                        selectedOption === PurchaseActionOption.MODIFY_GUESTS ||
                        selectedRefundOption === RefundOption.CHANGE_GUEST_QUANTITY ||
                        selectedRefundOption === RefundOption.CHANGE_EQUIPMENT_QUANTITY
                    }
                    shouldInvalidate={false}
                    onClose={handleChangeQuantityModalClose}
                />
            )}

            {isExperiencePurchaseItem(item) && (
                <SwitchExperienceModal
                    purchase={purchase}
                    item={item}
                    isOpen={selectedOption === PurchaseActionOption.SWITCH_EXPERIENCE}
                    onClose={(newPurchase, nextRefundItem) => {
                        if (newPurchase?.balance && newPurchase.balance < 0) {
                            setReturnPaymentSource(ReturnPaymentSource.SWITCH_EXPERIENCE);
                            setRefundItem(nextRefundItem ?? null);
                        } else {
                            setReturnPaymentSource(null);
                            setRefundItem(null);
                        }

                        onCloseAfterPurchaseChange(newPurchase);
                    }}
                />
            )}

            <CancelPurchaseItemModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.CANCEL_RESERVATION}
                onClose={handleCancelPurchaseItemModalClose}
            />

            <RemoveMembershipCodeModal
                isOpen={selectedOption === PurchaseActionOption.REMOVE_MEMBERSHIP_CODE}
                purchase={purchase}
                item={item}
                onClose={handleRemoveMembershipCodeModalClose}
            />

            {purchase.splitPayment.enabled && !isOneBuyerWithSamePaymentMethod(purchase) ? (
                <SplitPayRefundModal
                    purchase={purchase}
                    item={item}
                    shouldReduce={selectedRefundOption === RefundOption.REDUCE_AND_RETURN}
                    isOpen={
                        selectedRefundOption === RefundOption.RETURN_PAYMENT ||
                        selectedRefundOption === RefundOption.REDUCE_AND_RETURN
                    }
                    onClose={handleSplitPayRefundClose}
                />
            ) : (
                <PurchaseReturnPaymentModal
                    purchase={modifiedPurchase ?? purchase}
                    item={refundModalItem}
                    event={event}
                    refundAmount={refundModalAmount}
                    returnPaymentSource={returnPaymentSource}
                    shouldReduce={selectedRefundOption === RefundOption.REDUCE_AND_RETURN}
                    isOpen={
                        selectedRefundOption === RefundOption.RETURN_PAYMENT ||
                        selectedRefundOption === RefundOption.REDUCE_AND_RETURN
                    }
                    onClose={handlePurchaseReturnPaymentClose}
                />
            )}

            <PurchaseReduceBookingValueModal
                purchase={modifiedPurchase ?? purchase}
                item={item}
                isOpen={selectedRefundOption === RefundOption.REDUCE}
                onClose={onResetRefundOption}
            />

            <LinkWaiverModal
                purchaseItem={item}
                isOpen={selectedOption === PurchaseActionOption.LINK_WAIVER_FOR_PURCHASE}
                onClose={onModalClose}
            />

            <LinkWaiverParticipantModal
                isOpen={selectedOption === PurchaseActionOption.LINK_WAIVER_FOR_GUEST}
                guest={guest!}
                purchaseItem={item}
                onClose={onModalClose}
            />

            <EditCustomLineItemListModal
                purchase={purchase}
                item={item}
                isOpen={selectedOption === PurchaseActionOption.CHARGE}
                invalidateOnClose={false}
                onClose={onCloseAfterPurchaseChange}
            />

            {isExperiencePurchaseItem(item) && (
                <EditPurchaseAddOnsModal
                    purchase={purchase}
                    item={item}
                    isOpen={selectedOption === PurchaseActionOption.EDIT_ADD_ONS}
                    onClose={handleEditAddOnsModalClose}
                />
            )}

            <CollectPurchaseBalanceModal
                purchase={modifiedPurchase ?? purchase}
                event={event}
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE}
                onClose={handleMoveToUnassignGuideOrInvalidateAndFlush}
            />

            {isExperiencePurchaseItem(item) && (
                <CollectPurchaseBalanceViaInvoiceModal
                    purchase={modifiedPurchase ?? purchase}
                    event={event}
                    item={item}
                    isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                    onClose={handleMoveToUnassignGuideOrInvalidateAndFlush}
                />
            )}

            {shouldRenderMerchandiseQuantityModal(item, selectedRefundOption, merchandiseItem) && (
                <UpdateMerchandiseQuantityModal
                    purchase={purchase}
                    item={merchandiseItem!}
                    isOpen={
                        selectedOption === PurchaseActionOption.UPDATE_QUANTITY ||
                        selectedRefundOption === RefundOption.CHANGE_MERCHANDISE_QUANTITY
                    }
                    shouldInvalidate={false}
                    onClose={(
                        nextModalOption: PurchaseActionOption | null,
                        nextRefundOption: RefundOption | null,
                        refundAmount,
                        newPurchase,
                        nextRefundItem,
                    ) => {
                        const shouldOpenReturnPayment = refundAmount < 0;
                        const resolvedRefundOption = shouldOpenReturnPayment
                            ? RefundOption.RETURN_PAYMENT
                            : nextRefundOption;

                        if (newPurchase) {
                            setModifiedPurchase(newPurchase);
                        }

                        setSelectedOption(nextModalOption);
                        setSelectedRefundOption(resolvedRefundOption);
                        setReturnPaymentSource(
                            resolvedRefundOption === RefundOption.RETURN_PAYMENT
                                ? ReturnPaymentSource.CHANGE_MERCHANDISE_QUANTITY
                                : null,
                        );
                        setRefundItem(nextRefundItem ?? null);

                        if (!nextModalOption && !resolvedRefundOption) {
                            onPurchaseInvalidate();
                        }

                        setRefundAmount(refundAmount);
                    }}
                />
            )}

            {event && (
                <UnassignEventGuidePrompt
                    isOpen={selectedOption === PurchaseActionOption.UNASSIGN_GUIDE}
                    events={event}
                    onClose={handleUnassignGuidePromptClose}
                />
            )}
        </>
    );
};
